import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { Notification } from '@common/typescript/objects/Notification';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { Chat} from '@common/react/objects/Chat';
import { BaseApplicationState } from '@common/react/store';
import { subscribe } from '@common/react/utils/SignalRUtils/SignalRUtils';

import '@common/react/scss/components/typingComponent.scss';
import { useChatSettingsProviderContext } from '@common/react/components/Chat/ChatSettingsProvider';

interface Props {
	chat: Chat;
	render: (contacts) => React.ReactNode;
	delay?: number;
}

interface ContactTyping {
	userId: number;
	timer: any;
}

const TypingComponent: React.FC<Props> = ({chat, delay = 5000, render}) => {
	const [contactIds, setContacts] = React.useState<number[]>([]);
	const contactsTyping = React.useRef<ContactTyping[]>([]);
	const user = useSelector((state: BaseApplicationState<BaseUser>) => state.login.user, shallowEqual);

	const context = useChatSettingsProviderContext();

	if (!context?.state) throw 'need ChatSettingsContext';

	const {state: {notificationTypes}} = context;

	const handleNotification = React.useCallback((notification: Notification<BaseUser>) => {
		if (!user) return;
		switch (notification.objectType) {
			case notificationTypes.typing:
				const data = notification.data;
				const userId = data.user;
				if (user.id === userId || chat.id !== data.chatId) return;

				const item = contactsTyping.current.find(item => item.userId === userId);
				const newItem = item ? item : {userId, timer: undefined};
				if (!item) {
					contactsTyping.current.push(newItem);
					setContacts(contactsTyping.current.map(item => item.userId));
				}

				newItem.timer && clearTimeout(newItem.timer);

				newItem.timer = setTimeout(() => {
					contactsTyping.current = contactsTyping.current.filter(item => item.userId !== userId);
					setContacts(contactsTyping.current.map(item => item.userId));
				}, delay);

				break;
		}
	}, [user?.id, chat?.id]);

	React.useEffect(subscribe(handleNotification));

	React.useEffect(() => {
		contactsTyping.current.forEach(item => item.timer && clearTimeout(item.timer));
		contactsTyping.current = [];
		setContacts([]);
	}, [user?.id, chat?.id]);

	const contacts = React.useMemo(() => {
		if (!contactIds.length) return [];

		return chat.contacts.filter(contact => contactIds.find(id => contact.id === id));
	}, [contactIds, chat?.id, chat?.contacts?.length]);

	return <>
		{render(contacts)}
	</>;
};

export default TypingComponent;
